"use client";

import Image from "next/image";
import React from "react";

const SECTION_MIN_HEIGHT_PX = 280;

export interface WUniqueSellingPointsStripCard {
  iconUrl?: string;
  iconAlt?: string;
  count?: string;
  description?: string;
  active?: boolean;
}

export interface WUniqueSellingPointsStripProps {
  backgroundImageUrl?: string;
  overlayOpacity?: string;
  cards?: WUniqueSellingPointsStripCard[];
}

export default function WUniqueSellingPointsStrip({
  backgroundImageUrl,
  overlayOpacity = "0.4",
  cards = [],
}: WUniqueSellingPointsStripProps) {
  const overlayValue = overlayOpacity ? parseFloat(String(overlayOpacity)) : 0.4;
  const validCards = cards.filter(
    (c) =>
      c?.active !== false &&
      ((c?.count != null && String(c.count).trim() !== "") ||
        (c?.description != null && String(c.description).trim() !== ""))
  );

  if (validCards.length === 0) return null;

  return (
    <section
      className="relative w-full overflow-hidden"
      style={{ minHeight: SECTION_MIN_HEIGHT_PX }}
      aria-label="Unique selling points"
    >
      {/* Full-viewport fixed background – static on scroll, visible only within this section */}
      {backgroundImageUrl && (
        <div
          className="absolute inset-0 bg-gray-200"
          style={{
            backgroundImage: `url(${backgroundImageUrl})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundAttachment: "fixed",
          }}
          aria-hidden
        />
      )}

      {/* Optional dark overlay for text readability */}
      {overlayValue > 0 && (
        <div
          className="absolute inset-0 bg-black"
          style={{ opacity: overlayValue }}
          aria-hidden
        />
      )}

      {/* Content */}
      <div className="relative z-10 flex min-h-[280px] w-full flex-col items-center justify-center py-8 pl-7 pr-4 md:min-h-[300px] md:px-6 md:py-10">
        <div className="mx-auto flex w-full max-w-6xl flex-col flex-wrap items-stretch justify-center gap-6 md:flex-row md:gap-8 lg:gap-12">
          {validCards.map((card, index) => (
            <div
              key={index}
              className="flex w-full flex-row items-center gap-5 text-left md:w-auto md:flex-col md:gap-0 md:text-center"
            >
              {card.iconUrl && (
                <div className="flex shrink-0 items-center justify-center md:mb-3">
                  <Image
                    src={card.iconUrl}
                    alt={card.iconAlt || ""}
                    width={56}
                    height={56}
                    className="object-contain brightness-0 invert"
                    sizes="56px"
                    unoptimized={
                      card.iconUrl.startsWith("http") &&
                      !card.iconUrl.includes("trichos")
                    }
                  />
                </div>
              )}
              <div className="flex min-w-0 flex-1 flex-col md:flex-none md:items-center">
                {card.count != null && String(card.count).trim() !== "" && (
                  <div className="text-2xl font-extrabold tracking-tight text-white drop-shadow-md md:mb-1 md:text-4xl">
                    {String(card.count).trim()}
                  </div>
                )}
                {card.description != null &&
                  String(card.description).trim() !== "" && (
                    <p className="text-sm leading-snug text-white/95 drop-shadow md:max-w-[260px] md:text-base">
                      {String(card.description).trim()}
                    </p>
                  )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
